import _ from 'lodash';
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { eventHandlerUtils } from '../../libs/utils';
import {
  IUseForm,
  IUseFormProps,
  TChangeValueFunction,
  TChangeValuesFunction,
  TFormErrors,
  TRegisterValueFunction,
  TResetValuesConfig,
  TSubmitFunction
} from './types';
import { getJoiItemErrorMessage, PREDEFINED_SET_VALUE_FUNCTIONS } from './utils';

const INITIAL_VALUES = {};

type TOnSubmitFunction<T extends Record<string, any>> = (values: T) => void;

export function useForm<T extends Record<string, any>>(props: IUseFormProps<T>): IUseForm<T> {
  const {
    initialValues: propInitialValues = INITIAL_VALUES as any,
    onSubmit = () => {},
    validationSchema,
    readOnly,
    resetTriggers
  } = props;

  const initialValues = propInitialValues || INITIAL_VALUES;

  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<TFormErrors<T>>({});
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setValues(initialValues);
  }, [...(resetTriggers ? resetTriggers : [initialValues])]);

  const onChangeValues: TChangeValuesFunction<T> = (changedValues, config) => {
    setValues((prevValues) => ({
      ...prevValues,
      ...changedValues
    }));

    if (config?.submitOnChange) {
      validateAndSubmit({
        ...values,
        ...changedValues
      });
    }
  };

  const onSubmitValues: TSubmitFunction<T> = (submittedValues) => {
    return validateAndSubmit(submittedValues as T);
  };

  const submit: TSubmitFunction<T> = (updatedValues) => {
    onSubmit(
      {
        ...values,
        ...updatedValues
      },
      updatedValues
    );
  };

  const resetValues = (config?: TResetValuesConfig) => {
    setValues(initialValues);

    if (config?.submit) {
      validateAndSubmit(initialValues);
    }
  };

  const onChangeValue: TChangeValueFunction<T> = (key, value, config) => {
    setValues((prevValues) => ({
      ...prevValues,
      [key]: value
    }));

    if (config?.submitOnChange) {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      if (config.submitDebounce) {
        debounceTimeoutRef.current = setTimeout(() => {
          onSubmit({
            ...values,
            [key]: value
          });
        }, config.submitDebounce);
      } else {
        onSubmit({
          ...values,
          [key]: value
        });
      }
    }
  };

  const registerField: TRegisterValueFunction<T> = (key, config) => {
    return {
      value: config?.mapValue ? config.mapValue(values[key]) : values[key],
      onChange: (data: any) => {
        if (config?.setValueAs) {
          const setValueAs =
            typeof config.setValueAs === 'string'
              ? PREDEFINED_SET_VALUE_FUNCTIONS[config.setValueAs]
              : config.setValueAs;
          onChangeValue(key, setValueAs(data), config);
          return;
        }

        eventHandlerUtils.createEventTargetValueHandler((value: any) => onChangeValue(key, value, config))(data);
      },
      name: key,
      ...(config?.valueKey ? { [config.valueKey]: values[key] } : {})
    };
  };

  const validateAndSubmit = (submittedValues: T, callback?: TOnSubmitFunction<T>) => {
    if (validationSchema) {
      const { error } = validationSchema.validate(submittedValues, { abortEarly: false });

      if (error) {
        const formErrors = error.details.reduce<TFormErrors<T>>((acc, joiError) => {
          const key = joiError.context?.key as keyof T;
          const message = getJoiItemErrorMessage(joiError);

          if (key && message) {
            acc[key] = { message };
          }

          return acc;
        }, {});

        if (!_.isEmpty(formErrors)) {
          setErrors(formErrors);
          return;
        }
      }
    }

    onSubmit(submittedValues);
    if (callback) {
      callback(submittedValues);
    }
  };

  const formSubmitHandler = (callback: TOnSubmitFunction<T>) => (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    validateAndSubmit(values, callback);
  };

  const isDirty = useMemo(() => {
    return !_.isEqual(values, initialValues);
  }, [values, initialValues]);

  return {
    setValues,
    register: registerField,
    onChangeValue,
    resetValues,
    onSubmitValues,
    onChangeValues,
    values,
    formSubmitHandler,
    submit,
    isDirty,
    errors,
    readOnly,
    isInitialized: Object.keys(values).length > 0,
    initialValues
  };
}
